import axiosInstance from "./axiosInstance";
import { Unit, UnitDimension, UnitSystem, UnitConversionResult } from "../types/Unit";

function authHeader(token: string) {
    return { Authorization: `Bearer ${token}` };
}

// ---- UnitSystem ----

export async function getAllUnitSystems(token: string): Promise<UnitSystem[]> {
    const res = await axiosInstance.get<UnitSystem[]>("/unit-systems", { headers: authHeader(token) });
    return res.data;
}

export async function createUnitSystem(token: string, payload: { name: string; description?: string }): Promise<UnitSystem> {
    const res = await axiosInstance.post<UnitSystem>("/unit-systems", payload, { headers: authHeader(token) });
    return res.data;
}

export async function updateUnitSystem(token: string, payload: { id: string; version: number; name?: string; description?: string }): Promise<UnitSystem> {
    const res = await axiosInstance.patch<UnitSystem>("/unit-systems", payload, { headers: authHeader(token) });
    return res.data;
}

export async function deleteUnitSystem(token: string, id: string, version: number): Promise<void> {
    await axiosInstance.delete("/unit-systems", { headers: authHeader(token), data: { id, version } });
}

// ---- UnitDimension ----

export async function getAllUnitDimensions(token: string): Promise<UnitDimension[]> {
    const res = await axiosInstance.get<UnitDimension[]>("/unit-dimensions", { headers: authHeader(token) });
    return res.data;
}

export async function createUnitDimension(token: string, payload: { name: string; description?: string; baseUnitId?: string | null; convertible?: boolean; allowMultiplePerSystem?: boolean }): Promise<UnitDimension> {
    const res = await axiosInstance.post<UnitDimension>("/unit-dimensions", payload, { headers: authHeader(token) });
    return res.data;
}

export async function updateUnitDimension(token: string, payload: { id: string; version: number; name?: string; description?: string; baseUnitId?: string | null; convertible?: boolean | null; allowMultiplePerSystem?: boolean | null }): Promise<UnitDimension> {
    const res = await axiosInstance.patch<UnitDimension>("/unit-dimensions", payload, { headers: authHeader(token) });
    return res.data;
}

export async function deleteUnitDimension(token: string, id: string, version: number): Promise<void> {
    await axiosInstance.delete("/unit-dimensions", { headers: authHeader(token), data: { id, version } });
}

// ---- Unit ----

export async function getAllUnits(token: string, dimensionId?: string, systemId?: string): Promise<Unit[]> {
    const params: Record<string, string> = {};
    if (dimensionId) params.dimensionId = dimensionId;
    if (systemId) params.systemId = systemId;
    const res = await axiosInstance.get<Unit[]>("/units", { headers: authHeader(token), params });
    return res.data;
}

export async function createUnit(token: string, payload: { name: string; description?: string; unitDimensionId: string; unitSystemId: string; toBaseFactor?: number | null; toBaseOffset?: number | null }): Promise<Unit> {
    const res = await axiosInstance.post<Unit>("/units", payload, { headers: authHeader(token) });
    return res.data;
}

export async function updateUnit(token: string, payload: { id: string; version: number; name?: string; description?: string; unitDimensionId?: string; unitSystemId?: string; toBaseFactor?: number | null; toBaseOffset?: number | null }): Promise<Unit> {
    const res = await axiosInstance.patch<Unit>("/units", payload, { headers: authHeader(token) });
    return res.data;
}

export async function deleteUnit(token: string, id: string, version: number): Promise<void> {
    await axiosInstance.delete("/units", { headers: authHeader(token), data: { id, version } });
}

/** Convert a value from one unit to another within the same dimension. */
export async function convertUnit(token: string, value: number, fromUnitId: string, toUnitId: string): Promise<UnitConversionResult> {
    const res = await axiosInstance.get<UnitConversionResult>("/units/convert", {
        headers: authHeader(token),
        params: { value, from: fromUnitId, to: toUnitId },
    });
    return res.data;
}

// ---- Export / Import ----

export async function exportUnit(token: string, id: string): Promise<unknown> {
    const res = await axiosInstance.get(`/units/${id}/export`, { headers: authHeader(token) });
    return res.data;
}

export async function importUnit(token: string, dto: unknown): Promise<Unit> {
    const res = await axiosInstance.post<Unit>("/units/import", dto, { headers: authHeader(token) });
    return res.data;
}

export async function exportUnitSystem(token: string, id: string): Promise<unknown> {
    const res = await axiosInstance.get(`/unit-systems/${id}/export`, { headers: authHeader(token) });
    return res.data;
}

export async function importUnitSystem(token: string, dto: unknown): Promise<UnitSystem> {
    const res = await axiosInstance.post<UnitSystem>("/unit-systems/import", dto, { headers: authHeader(token) });
    return res.data;
}

export async function exportUnitDimension(token: string, id: string): Promise<unknown> {
    const res = await axiosInstance.get(`/unit-dimensions/${id}/export`, { headers: authHeader(token) });
    return res.data;
}

export async function importUnitDimension(token: string, dto: unknown): Promise<UnitDimension> {
    const res = await axiosInstance.post<UnitDimension>("/unit-dimensions/import", dto, { headers: authHeader(token) });
    return res.data;
}

type TransferResult = { succeeded: string[]; failed: { categoryName: string; reason: string }[] };

export async function transferUnits(token: string, categoryIds: string[], targetUrl: string, targetToken: string): Promise<TransferResult> {
    const res = await axiosInstance.post<TransferResult>("/units/transfer", { categoryIds, targetUrl, targetToken }, { headers: authHeader(token) });
    return res.data;
}

export async function transferUnitSystems(token: string, categoryIds: string[], targetUrl: string, targetToken: string): Promise<TransferResult> {
    const res = await axiosInstance.post<TransferResult>("/unit-systems/transfer", { categoryIds, targetUrl, targetToken }, { headers: authHeader(token) });
    return res.data;
}

export async function transferUnitDimensions(token: string, categoryIds: string[], targetUrl: string, targetToken: string): Promise<TransferResult> {
    const res = await axiosInstance.post<TransferResult>("/unit-dimensions/transfer", { categoryIds, targetUrl, targetToken }, { headers: authHeader(token) });
    return res.data;
}
